import { t } from '@/i18next';
import {
  deleteOrganizationLogo,
  disableOrganizationTwoFactor,
  forceOrganizationTwoFactor
} from '@/api/organization';

import {
  CONFIRMATION_REQUIRED_MODAL,
  DELETE_ENTITY_MODAL,
  PASSWORD_CONFIRMATION_REQUIRED_MODAL
} from '@/constants/modals';

import { ORGANIZATION_API } from '@/constants/api';

import type { OrganizationSimplified } from '@/types/organizations';
import type { PasswordConfirmationParams } from '@/api/profile/types';
import type { PromiseResolve } from '@/types/helpers';
import type { UseModal } from '@/types/modals';
import { LOGIN_TYPES_NAMES, type LoginTypes } from "@/constants/user";

export function openDeleteLogoModal(
  openModal: UseModal['openModal'],
  organization: OrganizationSimplified
) {
  async function onDeleteLogoHandler(resolve: PromiseResolve) {
    const data = await deleteOrganizationLogo(organization.id);
    resolve(data);
  }

  openModal<typeof DELETE_ENTITY_MODAL>(DELETE_ENTITY_MODAL, {
    confirmAction: onDeleteLogoHandler,
    useStatusConfirmation: true,
    invalidateQueryKey: ORGANIZATION_API,
    name: organization.name,
    confirmTranslation: 'common.delete',
    modalMessage: 'organization.delete_logo',
    successMessage: 'common.deletedSuccessfully',
    successMessageParams: {
      entityType: t('common.logo_placeholder'),
      entityName: organization.name
    }
  });
}

export function openForceOrganizationTwoFactorConfirmationModal(
  openModal: UseModal['openModal'],
  organization: OrganizationSimplified,
  loginType: LoginTypes,
) {
  async function onForceOrganizationTwoFactorHandler(
    resolve: PromiseResolve,
    params: PasswordConfirmationParams
  ) {
    const data = await forceOrganizationTwoFactor(organization.id, params);

    resolve(data);
  }

  async function onForceOrganizationTwoFactorHandlerSso(
    resolve: PromiseResolve
  ) {
    const data = await forceOrganizationTwoFactor(organization.id);

    resolve(data);
  }

  if (loginType === LOGIN_TYPES_NAMES.TYPE_SSO) {
    openModal<typeof CONFIRMATION_REQUIRED_MODAL>(
      CONFIRMATION_REQUIRED_MODAL,
      {
        confirmAction: onForceOrganizationTwoFactorHandlerSso,
        useStatusConfirmation: true,
        successMessage: 'organization.two_factor_forced_successfully',
        title: 'organizationDashboard.force_two_factor_authentication'
      }
    );

    return;
  }

  openModal<typeof PASSWORD_CONFIRMATION_REQUIRED_MODAL>(
    PASSWORD_CONFIRMATION_REQUIRED_MODAL,
    {
      confirmAction: onForceOrganizationTwoFactorHandler,
      useStatusConfirmation: true,
      successMessage: 'organization.two_factor_forced_successfully',
      title: 'organizationDashboard.force_two_factor_authentication'
    }
  );
}

export function disableOrganizationTwoFactorConfirmationModal(
  openModal: UseModal['openModal'],
  organization: OrganizationSimplified,
  loginType: LoginTypes
) {
  async function onDisableOrganizationTwoFactorHandler(
    resolve: PromiseResolve,
    params: PasswordConfirmationParams
  ) {
    const data = await disableOrganizationTwoFactor(organization.id, params);

    resolve(data);
  }

  async function onDisableOrganizationTwoFactorHandlerSso(
    resolve: PromiseResolve,
  ) {
    const data = await disableOrganizationTwoFactor(organization.id);

    resolve(data);
  }

  if (loginType === LOGIN_TYPES_NAMES.TYPE_SSO) {
    openModal<typeof CONFIRMATION_REQUIRED_MODAL>(
      CONFIRMATION_REQUIRED_MODAL,
      {
        confirmAction: onDisableOrganizationTwoFactorHandlerSso,
        useStatusConfirmation: true,
        successMessage: 'organization.two_factor_disabled_successfully',
        title: 'organization.disable_two_factor'
      }
    );

    return;
  }

  openModal<typeof PASSWORD_CONFIRMATION_REQUIRED_MODAL>(
    PASSWORD_CONFIRMATION_REQUIRED_MODAL,
    {
      confirmAction: onDisableOrganizationTwoFactorHandler,
      useStatusConfirmation: true,
      successMessage: 'organization.two_factor_disabled_successfully',
      title: 'organization.disable_two_factor'
    }
  );
}
